import axios from "axios";
import { message as AntdAlert } from "antd";
import { put, select } from "redux-saga/effects";
import moment from "moment";
import * as actions from "../actions";
import { baseURL } from "../../../../globalUtils/axiosConfig";
import errorHandler from "../../../../components/unauthorizeHandler";

export function* handleFetchAllStockManagementRegisters({ data }) {
  try {
    const params = { ...data };

    if (params.userIds && Array.isArray(params.userIds) && params.userIds.length > 0) {
      if (params.userIds.length === 1) {
        params.userId = params.userIds[0];
        delete params.userIds;
      } else {
        params.userIds = params.userIds.join(",");
      }
    }

    if (params.stockRegisterFilters && params.stockRegisterFilters.length > 0) {
      params.stockRegisterFilters.forEach(filterItem => {
        const key = Object.keys(filterItem)[0];
        const value = filterItem[key];
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value;
        }
      });
      delete params.stockRegisterFilters;
    }

    const response = yield axios.get(`${baseURL}/stock-management-register`, {
      params: params,
      paramsSerializer: params => {
        return new URLSearchParams(
          Object.entries(params).flatMap(([key, value]) =>
            Array.isArray(value) ? value.map(v => [key, v]) : [[key, value]]
          )
        ).toString();
      }
    });

    if (response.status === 200) {
      yield put(actions.fetchAllStockManagementRegistersSuccess(response.data));
    } else {
      yield put(actions.fetchAllStockManagementRegistersError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error fetching stock management registers:", error);
    yield put(actions.fetchAllStockManagementRegistersError(error.response?.data || error.message));
    AntdAlert.error("Failed to fetch stock management registers");
  }
}

export function* handleFetchStockManagementRegister({ data }) {
  try {
    const response = yield axios.get(`${baseURL}/stock-management-register/${data}`);

    if (response.status === 200) {
      yield put(actions.fetchStockManagementRegisterSuccess(response.data));
    } else {
      yield put(actions.fetchStockManagementRegisterError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error fetching stock management register:", error);
    yield put(actions.fetchStockManagementRegisterError(error.response?.data || error.message));
    AntdAlert.error("Failed to fetch stock management register");
  }
}

export function* handleCreateStockManagementRegister({ data }) {
  try {
    const formData = new FormData();

    for (const [key, value] of Object.entries(data)) {
      if (key === "documents" && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(file => {
            if (file instanceof File) {
              formData.append('documents', file);
            }
          });
        } else if (value instanceof File) {
          formData.append('documents', value);
        }
        continue;
      }

      if (value && value._isAMomentObject) {
        formData.append(key, value.toISOString());
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    const response = yield axios.post(`${baseURL}/stock-management-register`, formData, {
      headers: {
        "content-type": "multipart/form-data",
      },
    });

    if (response.status === 201) {
      AntdAlert.success("Stock management register created successfully");
      yield put(actions.createStockManagementRegisterSuccess(response.data));
    } else {
      AntdAlert.error("Failed to create stock management register");
      yield put(actions.createStockManagementRegisterError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error creating stock management register:", error);
    yield put(actions.createStockManagementRegisterError(error.response?.data || error.message));
    AntdAlert.error("Failed to create stock management register");
  }
}

export function* handleUpdateStockManagementRegister({ data }) {
  try {
    const { id, ...updateData } = data;
    const formData = new FormData();

    // Handle file uploads and regular data
    for (const [key, value] of Object.entries(updateData)) {
      if (key === "documents" && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(file => {
            if (file instanceof File) {
              formData.append('documents', file);
            }
          });
        } else if (value instanceof File) {
          formData.append('documents', value);
        }
        continue;
      }

      if (value && value._isAMomentObject) {
        formData.append(key, value.toISOString());
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    const response = yield axios.patch(
      `${baseURL}/stock-management-register/${id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.status === 200) {
      AntdAlert.success("Stock management register updated successfully");
      yield put(actions.updateStockManagementRegisterSuccess(response.data));
    } else {
      AntdAlert.error("Failed to update stock management register");
      yield put(actions.updateStockManagementRegisterError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error updating stock management register:", error);
    yield put(actions.updateStockManagementRegisterError(error.response?.data || error.message));
    AntdAlert.error("Failed to update stock management register");
  }
}

export function* handleDeleteStockManagementRegister({ data }) {
  try {
    const response = yield axios.delete(`${baseURL}/stock-management-register/${data}`);

    if (response.status === 200 || response.status === 204) {
      AntdAlert.success("Stock management register deleted successfully");
      yield put(actions.deleteStockManagementRegisterSuccess(data));
    } else {
      AntdAlert.error("Failed to delete stock management register");
      yield put(actions.deleteStockManagementRegisterError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error deleting stock management register:", error);
    yield put(actions.deleteStockManagementRegisterError(error.response?.data || error.message));
    AntdAlert.error("Failed to delete stock management register");
  }
}

export function* handleFetchAllStockTakes({ data }) {
  try {
    const { registerId, ...params } = data;
    const response = yield axios.get(`${baseURL}/stock-management-register/${registerId}/stock-takes`, {
      params
    });

    const stockTakesData = Array.isArray(response.data) ? response.data : response.data?.data || [];

    if (response.status === 200) {
      yield put(actions.fetchAllStockTakesSuccess(registerId, stockTakesData));
    } else {
      yield put(actions.fetchAllStockTakesError(registerId, response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error fetching stock takes:", error);
    const { registerId } = data;
    yield put(actions.fetchAllStockTakesError(registerId, error.response?.data || error.message));
    AntdAlert.error("Failed to fetch stock takes");
  }
}

export function* handleCreateStockTake({ data }) {
  try {
    const { registerId, ...stockTakeData } = data;

    const response = yield axios.post(
      `${baseURL}/stock-management-register/${registerId}/stock-take`,
      stockTakeData
    );

    if (response.status === 201) {
      AntdAlert.success("Stock take created successfully");
      const allStockTakes = response.data.stockTakes || [];
      yield put(actions.createStockTakeSuccess(registerId, allStockTakes));
      yield put(actions.createStockTakeSuccess(registerId, response.data));
      const lastFilters = yield select(state => state.labsStockManagementReducer.lastFilters);
      yield put(actions.fetchAllStockManagementRegisters(lastFilters));
    } else {
      AntdAlert.error(`Failed to create stock take: ${response.data?.message || 'Unknown error'}`);
      yield put(actions.createStockTakeError(registerId, response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error creating stock take:", error);
    const { registerId } = data;
    const errorData = error.response?.data;
    const is404StockNotFound =
      error.response?.status === 404 &&
      errorData?.message &&
      errorData.message.toLowerCase().includes("stock not found");
    yield put(actions.createStockTakeError(registerId, errorData || error.message));
    if (!is404StockNotFound) {
      const errorMessage = errorData?.message || error.message || "Failed to create stock take";
      AntdAlert.error(errorMessage);
    }
  }
}

export function* handleUpdateStockTake({ data }) {
  try {
    const { registerId, stockTakeId, ...updateData } = data;

    const formData = new FormData();

    for (const [key, value] of Object.entries(updateData)) {
      if (key === "documents" && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(file => {
            if (file instanceof File) {
              formData.append('documents', file);
            }
          });
        } else if (value instanceof File) {
          formData.append('documents', value);
        }
        continue;
      }

      if (value && value._isAMomentObject) {
        formData.append(key, value.toISOString());
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    const response = yield axios.patch(
      `${baseURL}/stock-management-register/${registerId}/stock-take/${stockTakeId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.status === 200) {
      AntdAlert.success("Stock take updated successfully");
      yield put(actions.updateStockTakeSuccess(registerId, response.data));
      const lastFilters = yield select(state => state.labsStockManagementReducer.lastFilters);
      yield put(actions.fetchAllStockManagementRegisters(lastFilters));
    } else {
      AntdAlert.error("Failed to update stock take");
      yield put(actions.updateStockTakeError(registerId, response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error updating stock take:", error);
    const { registerId } = data;
    yield put(actions.updateStockTakeError(registerId, error.response?.data || error.message));
    AntdAlert.error("Failed to update stock take");
  }
}

export function* handleDeleteStockTake({ data }) {
  try {
    const { registerId, stockTakeId } = data;
    const response = yield axios.delete(
      `${baseURL}/stock-management-register/stock-take/${stockTakeId}`
    );

    if (response.status === 200) {
      AntdAlert.success("Stock take deleted successfully");
      yield put(actions.deleteStockTakeSuccess(stockTakeId));
      const lastFilters = yield select(state => state.labsStockManagementReducer.lastFilters);
      yield put(actions.fetchAllStockManagementRegisters(lastFilters));
    } else {
      AntdAlert.error("Failed to delete stock take");
      yield put(actions.deleteStockTakeError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error deleting stock take:", error);
    yield put(actions.deleteStockTakeError(error.response?.data || error.message));
    AntdAlert.error("Failed to delete stock take");
  }
}

// This existing watcher/action remains in use. The component dispatches
// fetchOmniCurrentStock({warehouseId, registerIds}) once for the displayed set.
// No per-register HTTP requests are made for cached snapshots.
// When registerIds is omitted, the old warehouse-current-stock API is retained.
export function* handleFetchOmniCurrentStock({ data }) {
  const { warehouseId, registerIds, sortBy, sortOrder } = data || {};
  try {
    if (!warehouseId) {
      throw new Error("warehouseId is required to fetch OMNI stock");
    }

    if (Array.isArray(registerIds) && registerIds.length > 0) {
      const ids = [...new Set(registerIds.map(Number))];
      if (ids.some(id => !Number.isSafeInteger(id) || id <= 0)) {
        throw new Error("registerIds must contain positive integer register IDs");
      }
      const response = yield axios.get(
        `${baseURL}/stock-management-register/omni-snapshots`,
        { params: { warehouseId, registerIds: ids.join(",") } }
      );

      if (response.status === 200 && response.data?.success && response.data?.registers) {
        yield put(actions.fetchOmniSnapshotsSuccess({
          warehouseId: Number(warehouseId),
          registers: response.data.registers,
          legacyStock: response.data.legacyStock || [],
        }));
      } else {
        throw new Error(response.data?.message || "Failed to fetch OMNI snapshots");
      }
      return;
    }

    // Original endpoint retained for screens that genuinely require current stock.
    const params = {};
    if (sortBy && sortOrder) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder === "ascend" ? "asc" : "desc";
    }
    const response = yield axios.get(
      `${baseURL}/stock-management-register/stock-by-warehouse/${warehouseId}`,
      { params }
    );
    if (response.status === 200 && response.data?.success) {
      yield put(actions.fetchOmniCurrentStockSuccess(response.data.stock || []));
    } else {
      throw new Error(response.data?.message || "Failed to fetch current OMNI stock");
    }
  } catch (error) {
    console.error("Error fetching OMNI stock/snapshots:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch OMNI stock";
    yield put(actions.fetchOmniCurrentStockError(
      errorMessage,
      Array.isArray(registerIds) && registerIds.length > 0 ? { warehouseId, registerIds } : null
    ));
    AntdAlert.error(errorMessage);
  }
}

export function* handleGenerateVarianceReport({ data }) {
  const { registerId } = data;
  try {
    const response = yield axios.patch(
      `${baseURL}/stock-management-register/${registerId}/variance-reports/bulk`
    );

    if (response.status === 201 || response.status === 200) {
      AntdAlert.success("Variance report generated successfully");
      yield put(actions.generateVarianceReportSuccess(registerId, response.data));
      // Refresh stock management registers to update quantity values
      const lastFilters = yield select(state => state.labsStockManagementReducer.lastFilters);
      yield put(actions.fetchAllStockManagementRegisters(lastFilters));
    } else {
      AntdAlert.error("Failed to generate variance report");
      yield put(actions.generateVarianceReportError(registerId, response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error generating variance report:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to generate variance report";
    yield put(actions.generateVarianceReportError(registerId, errorMessage));
    AntdAlert.error(errorMessage);
  }
}

export function* handleFetchAllVarianceReports({ data }) {
  try {
    const { registerId, ...params } = data;
    const response = yield axios.get(`${baseURL}/stock-management-register/${registerId}/variance-reports`, {
      params
    });

    if (response.status === 200) {
      yield put(actions.fetchAllVarianceReportsSuccess(registerId, response.data));
    } else {
      yield put(actions.fetchAllVarianceReportsError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    const { registerId } = data;
    console.log("Error fetching variance reports:", error);
    yield put(actions.fetchAllVarianceReportsError(registerId, error.response?.data || error.message));
    AntdAlert.error("Failed to fetch variance reports");
  }
}

export function* handleFetchOneVarianceReport({ data }) {
  try {
    const { registerId, id } = data;
    const response = yield axios.get(`${baseURL}/stock-management-register/${registerId}/variance-report/${id}`);

    if (response.status === 200) {
      yield put(actions.fetchOneVarianceReportSuccess(response.data));
    } else {
      yield put(actions.fetchOneVarianceReportError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error fetching one variance report:", error);
    yield put(actions.fetchOneVarianceReportError(error.response?.data || error.message));
    AntdAlert.error("Failed to fetch variance report");
  }
}

export function* handleUpdateVarianceReport({ data }) {
  try {
    const { registerId, id, ...updateData } = data;

    const formData = new FormData();

    for (const [key, value] of Object.entries(updateData)) {
      if (key === "documents" && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(file => {
            if (file instanceof File) {
              formData.append('documents', file);
            }
          });
        } else if (value instanceof File) {
          formData.append('documents', value);
        }
        continue;
      }

      if (key === "items" && Array.isArray(value)) {
        formData.append('items', JSON.stringify(value));
      } else if (value && (value._isAMomentObject || (typeof value.format === 'function' && typeof value.toDate === 'function'))) {
        formData.append(key, value.toISOString());
      } else if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }

    const response = yield axios.patch(
      `${baseURL}/stock-management-register/${registerId}/variance-report/${id}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.status === 200) {
      AntdAlert.success("Variance report updated successfully");
      yield put(actions.updateVarianceReportSuccess(registerId, response.data));
      yield put(actions.fetchAllVarianceReports(registerId));
      // Refresh stock management registers to update Status value
      const lastFilters = yield select(state => state.labsStockManagementReducer.lastFilters);
      yield put(actions.fetchAllStockManagementRegisters(lastFilters));
    } else {
      AntdAlert.error("Failed to update variance report");
      yield put(actions.updateVarianceReportError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error updating variance report:", error);
    yield put(actions.updateVarianceReportError(error.response?.data || error.message));
    AntdAlert.error("Failed to update variance report");
  }
}

export function* handleAddVarianceComment({ data }) {
  try {
    const { registerId, id, ...commentData } = data;

    const response = yield axios.post(
      `${baseURL}/stock-management-register/${registerId}/variance-report/${id}/comments`,
      commentData
    );

    if (response.status === 201 || response.status === 200) {
      AntdAlert.success("Comment added successfully");
      yield put(actions.addVarianceCommentSuccess(response.data));
      yield put(actions.fetchAllVarianceReports(registerId));
    } else {
      AntdAlert.error("Failed to add comment");
      yield put(actions.addVarianceCommentError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error adding comment to variance report:", error);
    yield put(actions.addVarianceCommentError(error.response?.data || error.message));
    AntdAlert.error("Failed to add comment");
  }
}

export function* handleDeleteVarianceAttachment({ data }) {
  try {
    const { registerId, reportId, attachmentId } = data;
    const response = yield axios.delete(
      `${baseURL}/stock-management-register/${registerId}/variance-report/${reportId}/attachments/${attachmentId}`
    );

    if (response.status === 200 || response.status === 204) {
      AntdAlert.success("Attachment deleted successfully");
      yield put(actions.deleteVarianceAttachmentSuccess(attachmentId));
      yield put(actions.fetchAllVarianceReports(registerId));
    } else {
      AntdAlert.error("Failed to delete attachment");
      yield put(actions.deleteVarianceAttachmentError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error deleting variance report attachment:", error);
    yield put(actions.deleteVarianceAttachmentError(error.response?.data || error.message));
    AntdAlert.error("Failed to delete attachment");
  }
}

export function* handleFetchVarianceHistory({ data }) {
  try {
    const { registerId, reportId } = data;
    const response = yield axios.get(`${baseURL}/stock-management-register/${registerId}/variance-report/${reportId}/history`);

    if (response.status === 200) {
      yield put(actions.fetchVarianceHistorySuccess(reportId, response.data));
    } else {
      yield put(actions.fetchVarianceHistoryError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error fetching variance history:", error);
    yield put(actions.fetchVarianceHistoryError(error.response?.data || error.message));
    AntdAlert.error("Failed to fetch variance history");
  }
}

export function* handleSyncLabStock() {
  try {
    const response = yield axios.post(`${baseURL}/firebird/sync/`);

    if (response.status === 200 || response.status === 201) {
      AntdAlert.success("Lab Stock Synced Successfully");
      
      const lastSyncResponse = yield axios.get(`${baseURL}/firebird/last-sync/`);
      const lastSynced = lastSyncResponse.data?.updatedAt || null;
      
      yield put(actions.syncLabStockSuccess({ lastSynced }));
    } else {
      AntdAlert.error("Failed to Sync Lab Stock");
      yield put(actions.syncLabStockError(response.data));
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("Error Syncing Lab Stock:", error);
    yield put(actions.syncLabStockError(error.response?.data || error.message));
    AntdAlert.error("Failed to Sync Lab Stock");
  }
}

export function* handleFetchLastSyncTime() {
  try {
    const response = yield axios.get(`${baseURL}/firebird/last-sync/`);
    if (response.status === 200) {
      const lastSynced = response.data?.updatedAt || null;
      yield put(actions.fetchLastSyncTimeSuccess({ lastSynced }));
    } else {
      yield put(actions.fetchLastSyncTimeError(response.data));
    }
  } catch (error) {
    console.log("Error fetching last sync time:", error);
    yield put(actions.fetchLastSyncTimeError(error.response?.data || error.message));
  }
}